import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTranslations(language);
  }, [language]);

  const loadTranslations = async (lang) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/i18n/translations/${lang}`);
      setTranslations(response.data.translations);
      localStorage.setItem('language', lang);
    } catch (error) {
      console.error('Failed to load translations:', error);
      // Fallback to English
      if (lang !== 'en') {
        loadTranslations('en');
      }
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async (newLang) => {
    setLanguage(newLang);
    try {
      await axios.post(`${API_URL}/api/i18n/preference`, { language: newLang }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save language preference:', error);
    }
  };

  // Translation function with fallback
  const t = (key, namespace = 'common') => {
    const namespaceTranslations = translations[namespace] || {};
    return namespaceTranslations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t, loading, translations }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Language Switcher Component
export const LanguageSwitcher = ({ className = '' }) => {
  const { language, changeLanguage } = useLanguage();
  const [languages, setLanguages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/i18n/languages`)
      .then(res => setLanguages(res.data.languages))
      .catch(err => console.error('Failed to load languages:', err));
  }, []);

  const currentLang = languages.find(l => l.code === language) || { flag: '🌐', native_name: 'Language' };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:border-purple-300 transition-colors text-sm"
      >
        <span>{currentLang.flag}</span>
        <span className="hidden sm:inline text-gray-700">{currentLang.native_name}</span>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
            {languages.map(lang => (
              <button
                key={lang.code}
                onClick={() => { changeLanguage(lang.code); setIsOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                  language === lang.code ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                }`}
              >
                <span className="text-lg">{lang.flag}</span>
                <div className="text-left">
                  <p className="text-sm font-medium">{lang.native_name}</p>
                  <p className="text-xs text-gray-500">{lang.name}</p>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageProvider;
